import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, MapPin, Bed, Bath, Maximize, ArrowRight, Map, Grid3x3 } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Navbar } from '@/components/Navbar';
import { formatPrice } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

import markerIconGreen from '../assets/images/marker-icon-green.png';
import markerShadow from '../assets/images/marker-shadow.png';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIconGreen,
  iconUrl: markerIconGreen,
  shadowUrl: markerShadow,
});

// Custom marker icon for properties
const propertyIcon = new L.Icon({
  iconUrl: markerIconGreen,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

import heroImage from '../assets/images/hero-properties.png';

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [filters, setFilters] = useState({
    search: '',
    property_type: '',
    city: '',
    min_price: '',
    max_price: ''
  });

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.city) params.append('city', filters.city);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);

      const response = await axios.get(`${API}/properties?${params.toString()}`);
      let data = response.data;

      if (filters.search) {
        data = data.filter(p =>
          p.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          p.location.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      setProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchProperties();
  };

  // Get map center based on properties
  const getMapCenter = () => {
    const validProperties = properties.filter(p => p.latitude && p.longitude);
    if (validProperties.length === 0) {
      return [11.0168, 76.9558]; // Default to Coimbatore
    }
    const avgLat = validProperties.reduce((sum, p) => sum + p.latitude, 0) / validProperties.length;
    const avgLng = validProperties.reduce((sum, p) => sum + p.longitude, 0) / validProperties.length;
    return [avgLat, avgLng];
  };

  return (
    <div className="min-h-screen">
      {/* Hero Header with Image */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Luxury Property"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Transparent Navbar */}
        <Navbar transparent={true} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-5xl md:text-6xl font-medium mb-4 premium-glow">Find Your Dream Property</h1>
            <p className="text-lg md:text-xl text-white/90">Explore our curated collection of premium properties across Tamil Nadu</p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-stone-200 py-6 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#57534E]" />
                  <Input
                    data-testid="search-input"
                    placeholder="Search by title or location..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-10 h-12 bg-white border-stone-200 focus:border-primary focus:ring-1 focus:ring-primary/20 rounded-sm"
                  />
                </div>
              </div>

              <Select value={filters.property_type} onValueChange={(value) => handleFilterChange('property_type', value)}>
                <SelectTrigger data-testid="property-type-filter" className="h-12 bg-white border-stone-200 rounded-sm">
                  <SelectValue placeholder="Property Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="agricultural">Agricultural</SelectItem>
                  <SelectItem value="land">Land</SelectItem>
                </SelectContent>
              </Select>

              <Input
                data-testid="city-filter"
                placeholder="City"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                className="h-12 bg-white border-stone-200 rounded-sm"
              />

              <Button
                data-testid="search-button"
                onClick={handleSearch}
                className="h-12 bg-primary text-white hover:bg-primary/90 rounded-sm font-medium"
              >
                <Search className="w-5 h-5 mr-2" />
                Search
              </Button>
            </div>

            {/* View Toggle */}
            <div className="flex justify-end gap-2">
              <Button
                data-testid="grid-view-button"
                onClick={() => setViewMode('grid')}
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                className={`h-10 ${viewMode === 'grid' ? 'bg-primary text-white' : 'border-stone-200 text-[#57534E]'}`}
              >
                <Grid3x3 className="w-5 h-5 mr-2" />
                Grid View
              </Button>
              <Button
                data-testid="map-view-button"
                onClick={() => setViewMode('map')}
                variant={viewMode === 'map' ? 'default' : 'outline'}
                className={`h-10 ${viewMode === 'map' ? 'bg-primary text-white' : 'border-stone-200 text-[#57534E]'}`}
              >
                <Map className="w-5 h-5 mr-2" />
                Map View
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : viewMode === 'grid' ? (
            // Grid View
            properties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {properties.map((property, index) => (
                  <motion.div
                    key={property.property_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <Link
                      to={`/properties/${property.property_id}`}
                      data-testid={`property-card-${property.property_id}`}
                      className="property-card group block bg-white border border-stone-100 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:border-primary/20 rounded-sm"
                    >
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={property.images[0] || 'https://images.unsplash.com/photo-1663756915301-2ba688e078cf?crop=entropy&cs=srgb&fm=jpg&q=85'}
                          alt={property.title}
                          className="property-card-image w-full h-full object-cover"
                        />
                        {property.featured && (
                          <div className="absolute top-4 right-4 bg-accent text-white px-3 py-1 rounded-sm text-sm font-medium">
                            Featured
                          </div>
                        )}
                        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-primary px-3 py-1 rounded-sm text-sm font-medium capitalize">
                          {property.property_type}
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="font-heading text-xl font-medium text-primary mb-2 line-clamp-1">
                          {property.title}
                        </h3>
                        <div className="flex items-center gap-2 text-[#57534E] mb-4">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{property.location}, {property.city}</span>
                        </div>

                        <div className="flex items-center gap-4 mb-4 text-sm text-[#57534E]">
                          {property.bedrooms && (
                            <div className="flex items-center gap-1">
                              <Bed className="w-4 h-4" />
                              <span>{property.bedrooms}</span>
                            </div>
                          )}
                          {property.bathrooms && (
                            <div className="flex items-center gap-1">
                              <Bath className="w-4 h-4" />
                              <span>{property.bathrooms}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Maximize className="w-4 h-4" />
                            <span>{property.area_sqft} sqft</span>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-stone-100">
                          <span className="text-2xl font-heading font-medium text-primary">
                            {formatPrice(property.price)}
                          </span>
                          <span className="text-accent font-medium flex items-center gap-1">
                            View Details
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-[#FFF8E7] rounded-full flex items-center justify-center">
                  <Search className="w-12 h-12 text-[#57534E]" />
                </div>
                <h3 className="font-heading text-2xl font-medium text-primary mb-2">No Properties Found</h3>
                <p className="text-[#57534E] mb-6">Try adjusting your filters or search criteria</p>
                <Button
                  data-testid="reset-filters-button"
                  onClick={() => {
                    setFilters({ search: '', property_type: '', city: '', min_price: '', max_price: '' });
                    fetchProperties();
                  }}
                  variant="outline"
                  className="border-2 border-primary text-primary hover:bg-primary hover:text-white"
                >
                  Reset Filters
                </Button>
              </div>
            )
          ) : (
            // Map View
            <div className="h-[600px] rounded-sm overflow-hidden border border-stone-200">
              <MapContainer
                center={getMapCenter()}
                zoom={10}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {properties.filter(p => p.latitude && p.longitude).map((property) => (
                  <Marker
                    key={property.property_id}
                    position={[property.latitude, property.longitude]}
                    icon={propertyIcon}
                  >
                    <Popup>
                      <div className="w-64">
                        <img
                          src={property.images[0] || 'https://images.unsplash.com/photo-1663756915301-2ba688e078cf?crop=entropy&cs=srgb&fm=jpg&q=85'}
                          alt={property.title}
                          className="w-full h-32 object-cover rounded mb-2"
                        />
                        <h3 className="font-heading text-lg font-medium text-primary mb-1">
                          {property.title}
                        </h3>
                        <p className="text-sm text-[#57534E] mb-2 flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {property.location}, {property.city}
                        </p>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xl font-heading font-medium text-primary">
                            {formatPrice(property.price)}
                          </span>
                          {property.area_sqft && (
                            <span className="text-sm text-[#57534E]">{property.area_sqft} sqft</span>
                          )}
                        </div>
                        <Link
                          to={`/properties/${property.property_id}`}
                          className="block w-full text-center bg-primary text-white py-2 rounded-sm text-sm font-medium hover:bg-primary/90 transition-colors"
                        >
                          View Details
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}